import { NAMES } from "helper/constant";
import React, { useMemo } from "react";
import clsx from "clsx";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie
} from "recharts";

const CHART_COLORS = [
  "#10b981",
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#6366f1"
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const name = NAMES[payload[0].name] || payload[0].name;
    return (
      <div className="gt-chart-tooltip">
        {name} — {payload[0].value}
      </div>
    );
  }
  return null;
};

const ChartLegend = ({ items, onItemClick, clickable }) => {
  if (!items || !items.length) return null;
  return (
    <div className="gt-chart-legend">
      {items.map((item, index) => (
        <div
          key={item.name}
          className={clsx(
            "gt-chart-legend__item",
            clickable && "gt-chart-legend__item--clickable"
          )}
          onClick={() => clickable && onItemClick && onItemClick(item)}
          onKeyDown={e =>
            clickable && onItemClick && e.key === "Enter" && onItemClick(item)
          }
          role={clickable ? "button" : undefined}
          tabIndex={clickable ? 0 : undefined}
        >
          <span
            className="gt-chart-legend__dot"
            style={{ background: CHART_COLORS[index % CHART_COLORS.length] }}
          />
          <span>{NAMES[item.name] || item.name}</span>
          <span className="gt-chart-legend__value">{item.userCount}</span>
        </div>
      ))}
    </div>
  );
};

export const DashboardChart = ({
  data = [],
  type = "BAR",
  isReceipt = false,
  handleReceiptClick,
  outerRadius = 88,
  innerRadius = 52,
  showLegend = true
}) => {
  const mapData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data
      .filter(el => el.count !== 0)
      .map(item => ({
        name: item.name,
        userCount: item.count
      }));
  }, [data]);

  const total = useMemo(
    () => mapData.reduce((sum, item) => sum + item.userCount, 0),
    [mapData]
  );

  if (!mapData.length) {
    return <div className="gt-chart-empty">No data available</div>;
  }

  return (
    <>
      {type === "BAR" && (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={mapData}
            margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f1f5f9"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(16,185,129,0.06)" }}
            />
            <Bar dataKey="userCount" radius={[6, 6, 0, 0]}>
              {mapData.map((entry, index) => (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}

      {type === "LINE" && (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={mapData}
            margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f1f5f9"
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="userCount"
              stroke="#10b981"
              strokeWidth={2.5}
              dot={{ fill: "#10b981", strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: "#059669" }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {type === "PIE" && (
        <>
          <div className="gt-chart-card__chart">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={mapData}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  paddingAngle={2}
                  dataKey="userCount"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {mapData.map((entry, index) => (
                    <Cell
                      key={`cell-${entry.name}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      role={isReceipt ? "button" : undefined}
                      style={isReceipt ? { cursor: "pointer" } : undefined}
                      onClick={() =>
                        isReceipt &&
                        handleReceiptClick &&
                        handleReceiptClick(entry, isReceipt)
                      }
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#0f172a"
                  fontSize="22"
                  fontWeight="700"
                  dy="-6"
                >
                  {total}
                </text>
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#94a3b8"
                  fontSize="11"
                  fontWeight="600"
                  dy="14"
                >
                  TOTAL
                </text>
              </PieChart>
            </ResponsiveContainer>
          </div>
          {showLegend && (
            <ChartLegend
              items={mapData}
              clickable={isReceipt}
              onItemClick={entry =>
                handleReceiptClick && handleReceiptClick(entry, isReceipt)
              }
            />
          )}
        </>
      )}
    </>
  );
};
